import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CANALI, MESI, fmtDate, nowPeriod } from "@/lib/format";
import { downloadFile } from "@/lib/download";
import { Field, PageHeader, PeriodSelect, SimpleSelect, inputCls } from "@/components/app/common";
import { MarcaSelect, useMarche } from "@/components/app/MarcaSelect";

const pad = (n) => String(n).padStart(2, "0");
const lastDay = (y, m) => new Date(y, m, 0).getDate();
const MODES = [["mese", "Mese"], ["anno", "Anno"], ["custom", "Intervallo personalizzato"]];

function computeRange(mode, period, custom) {
  if (mode === "mese") return { start: `${period.anno}-${pad(period.mese)}-01`, end: `${period.anno}-${pad(period.mese)}-${lastDay(period.anno, period.mese)}`, label: `${MESI[period.mese - 1]} ${period.anno}` };
  if (mode === "anno") return { start: `${period.anno}-01-01`, end: `${period.anno}-12-31`, label: `Anno ${period.anno}` };
  return { ...custom, label: custom.start && custom.end ? `dal ${fmtDate(custom.start)} al ${fmtDate(custom.end)}` : "—" };
}

const ExportCard = ({ icon: Icon, title, text, items, onClick, loading, testId, tone }) => (
  <button data-testid={testId} onClick={onClick} disabled={loading} className="card-soft p-6 text-left hover:shadow-md transition-shadow duration-200 disabled:opacity-60 group">
    <div className="flex items-center gap-3 mb-3">
      <span className={`h-11 w-11 rounded-full grid place-items-center ${tone}`}>{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}</span>
      <h3 className="font-display text-xl">{title}</h3>
    </div>
    <p className="text-sm text-[#756B64] mb-3">{text}</p>
    <ul className="text-xs text-[#5E4A42] space-y-1 list-disc pl-4">{items.map((i) => <li key={i}>{i}</li>)}</ul>
    <div className="mt-5 text-sm font-semibold text-[#8C6D62] group-hover:underline">{loading ? "Preparazione in corso…" : "Scarica →"}</div>
  </button>
);

export default function Esporta() {
  const [mode, setMode] = useState("mese");
  const [period, setPeriod] = useState(nowPeriod());
  const [custom, setCustom] = useState({ start: `${period.anno}-01-01`, end: `${period.anno}-${pad(period.mese)}-${lastDay(period.anno, period.mese)}` });
  const [canale, setCanale] = useState("all");
  const [marca, setMarca] = useState("all");
  const marche = useMarche();
  const [busy, setBusy] = useState(null);
  const range = computeRange(mode, period, custom);

  const run = async (kind) => {
    if (!range.start || !range.end || range.end < range.start) return toast.error("Controlla le date dell'intervallo");
    setBusy(kind);
    try {
      await downloadFile(`/export/${kind}`, { start: range.start, end: range.end, ...(canale !== "all" ? { canale } : {}), ...(marca !== "all" ? { marca_id: marca } : {}) }, `report.${kind === "excel" ? "xlsx" : "pdf"}`);
      toast.success("Download avviato");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="esporta-page">
      <PageHeader overline="Report" title="Esporta dati" subtitle="Scarica i dati in Excel o un report PDF pronto da inviare al commercialista." />
      <section className="card-soft p-5 sm:p-7 mb-6 space-y-5">
        <div className="flex flex-wrap gap-2 p-1 rounded-full bg-[#F4EFEA] w-fit" data-testid="export-mode-toggle">
          {MODES.map(([v, l]) => (
            <button key={v} data-testid={`export-mode-${v}`} onClick={() => setMode(v)} className={`px-4 h-9 rounded-full text-sm transition-colors ${mode === v ? "bg-white shadow-sm font-semibold" : "text-[#756B64] hover:text-[#2C2623]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {mode === "mese" && <PeriodSelect anno={period.anno} mese={period.mese} onChange={(p) => setPeriod({ ...p, mese: p.mese || 1 })} prefix="export" />}
          {mode === "anno" && <PeriodSelect anno={period.anno} mese={null} allowYear onChange={(p) => setPeriod((s) => ({ ...s, anno: p.anno }))} prefix="export-anno" />}
          {mode === "custom" && (
            <>
              <Field label="Dal"><Input type="date" data-testid="export-start-input" value={custom.start} onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} className={`${inputCls} w-44`} /></Field>
              <Field label="Al"><Input type="date" data-testid="export-end-input" value={custom.end} onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} className={`${inputCls} w-44`} /></Field>
            </>
          )}
          <SimpleSelect testId="export-channel-select" className="w-44" value={canale} onChange={setCanale} options={[{ value: "all", label: "Tutti i canali" }, ...CANALI.map((c) => ({ value: c, label: c }))]} />
          <MarcaSelect marche={marche} value={marca} onChange={setMarca} testId="export-marca-select" />
        </div>
        <p className="text-sm text-[#756B64]" data-testid="export-range-label">Periodo selezionato: <b className="text-[#2C2623]">{range.label}</b>{canale !== "all" ? ` · ${canale}` : ""}{marca !== "all" ? ` · ${marche.find((m) => m.id === marca)?.nome || ""} (solo sezioni prodotti e magazzino)` : ""}</p>
      </section>
      <div className="grid md:grid-cols-2 gap-5">
        <ExportCard testId="export-excel-button" icon={FileSpreadsheet} tone="bg-[#8AA090]/15 text-[#4E6656]" title="Excel (.xlsx)" text="Tutti i dati del periodo, pronti per analisi e filtri." loading={busy === "excel"} onClick={() => run("excel")}
          items={["Riepilogo: KPI, IVA e confronto canali", "Movimenti con imponibile, IVA e totale", "Costi per categoria", "Margini per marca", "Margini per tipologia", "Prodotti e margini (con marca)", "Magazzino: giacenze attuali e valore a costo medio", "Spese ricorrenti"]} />
        <ExportCard testId="export-pdf-button" icon={FileText} tone="bg-[#E8C5C8]/50 text-[#77594E]" title="Report PDF" text="Un documento ordinato per il commercialista." loading={busy === "pdf"} onClick={() => run("pdf")}
          items={["Indicatori principali del periodo", "Liquidazione IVA: debito, credito e saldo", "Costi per categoria e confronto canali", "Margini per marca, per prodotto e per tipologia"]} />
      </div>
    </div>
  );
}
